import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const MAIN_SITE = "https://www.burgerlijk.com";

// Same menu as burgerlijk.com, so moving between both sites feels like one site
const SITE_NAV = [
  { label: "Home", href: `${MAIN_SITE}/` },
  { label: "Over ons", href: `${MAIN_SITE}/over-ons` },
  { label: "De Burger Box", href: `${MAIN_SITE}/burger-box` },
  { label: "On Tour", href: "/", current: true },
  { label: "Zonder Ende", href: `${MAIN_SITE}/zonder-ende` },
  { label: "Contact", href: `${MAIN_SITE}/contact` },
];

const TOUR_NAV = [
  { label: "Formules", to: "/#formules" },
  { label: "Werking & info", to: "/werking-info" },
  { label: "Offerte aanvragen", to: "/offerte" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setMenuOpen(false), [location.pathname, location.hash]);

  return (
    <header className="relative z-40">
      <div className="bg-bl-brown py-2 text-xs text-white sm:text-sm">
        <div className="page-container flex justify-center gap-6 sm:gap-8">
          <span className="hidden items-center gap-1.5 sm:flex">
            <PinIcon />
            Comm. Edm. Ameyestraat 4 - 8540 Deerlijk
          </span>
          <a href="mailto:ontour@burgerlijk.com" className="flex items-center gap-1.5 hover:opacity-80">
            <MailIcon />
            ontour@burgerlijk.com
          </a>
        </div>
      </div>

      <div className="bg-white">
        <div className="page-container flex items-center justify-between py-3">
          <a href={`${MAIN_SITE}/`} className="flex items-center" aria-label="Burgerlijk">
            <img src="/images/logo.png" alt="Burgerlijk" className="h-12 w-auto sm:h-14" />
          </a>

          <nav className="hidden items-center gap-6 lg:flex xl:gap-8" aria-label="Burgerlijk">
            {SITE_NAV.map((item) =>
              item.current ? (
                <Link
                  key={item.label}
                  to={item.href}
                  aria-current="page"
                  className="relative text-sm font-medium text-bl-brown after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-full after:bg-bl-brown"
                >
                  {item.label}
                </Link>
              ) : (
                <a key={item.label} href={item.href} className="text-sm text-bl-black transition-colors hover:text-bl-brown">
                  {item.label}
                </a>
              )
            )}
          </nav>

          <Link to="/offerte" className="btn-red hidden px-6 py-2.5 lg:inline-flex">
            Offerte aanvragen
          </Link>

          <button
            type="button"
            className="rounded p-2 lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? "Menu sluiten" : "Menu openen"}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              {menuOpen ? (
                <path d="M6 6l12 12M18 6L6 18" />
              ) : (
                <path d="M3 6h18M3 12h18M3 18h18" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* On Tour sub navigation */}
      <div className="hidden bg-bl-black lg:block">
        <nav className="page-container flex items-center gap-8 py-2.5 text-sm" aria-label="On Tour">
          <span className="font-display font-bold uppercase tracking-wider text-bl-gold">On Tour</span>
          {TOUR_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                `transition-colors ${isActive && !item.to.includes("#") ? "text-white" : "text-white/60 hover:text-white"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {menuOpen && (
        <nav id="mobile-menu" className="border-t border-bl-black/10 bg-white lg:hidden" aria-label="Menu">
          <div className="page-container space-y-6 py-5">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-bl-brown">On Tour</p>
              {TOUR_NAV.map((item) => (
                <Link key={item.to} to={item.to} className="block py-2 text-lg font-medium">
                  {item.label}
                </Link>
              ))}
            </div>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-bl-black/50">Burgerlijk</p>
              {SITE_NAV.filter((item) => !item.current).map((item) => (
                <a key={item.label} href={item.href} className="block py-2 text-bl-black/80">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}

function PinIcon() {
  return (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
    </svg>
  );
}
